import { ServerEventsClient } from '@servicestack/client';
import { Button, Text } from '@zinc/ui';
import { ToastUtil } from 'bz-core-controls/controls/utils/toast';
import AuthStore from 'bz-stores/lib/auth-store';
import useUnmountEffect from 'bz-utilities/hooks/unmount-effect';
import history from 'bz-utilities/modules/networking/history/rr4';
import { useState } from 'react';
import { NotificationMessageEvent } from 'shell/notifications/events';
import {
  SystemNotificationObjectType,
  type SystemNotificationResponse,
  SystemNotificationType,
  TaskResponse
} from 'tm3-core/api.dtos';
import { cache } from 'tm3-core/cache';
import { TaskUpdateEvent } from 'tm3-core/events/task';
import { v4 as uuid } from 'uuid';

interface IProps {}

const ApiNotifications: React.FC<IProps> = () => {
  const [serverEventsClient] = useState(() => {
    if (!AuthStore.user.clientId) return;
    const channels = [`${AuthStore.user.clientId}/channels/notifications`];

    const client = new ServerEventsClient('/api/', channels, {
      handlers: {
        SystemNotification: (res: SystemNotificationResponse) => {
          if (res.userId && AuthStore.user.id !== res.userId.toString()) return;
          cache.mediator.publish(new NotificationMessageEvent());
          // When the notification is for a task-count change (silent, created by
          // ApplyAutoRules), also publish a TaskUpdateEvent so that the task list
          // on the relevant record page refreshes in-place without a full reload.
          // ObjectId = target entity ID; Url = target entity type (e.g. "Case").

          if (res.objectType === SystemNotificationObjectType.Tasks && res.objectId && res.url) {
            cache.mediator.publish(
              new TaskUpdateEvent({
                task: new TaskResponse({ targetType: res.url, targetId: res.objectId }),
                isNew: true
              })
            );
          }
          if (!res.notify) return;
          const id = uuid();
          let content = <Text>{res.message}</Text>;
          if (res.url)
            content = (
              <Button
                variant="outline"
                size="small"
                onClick={() => {
                  history.push(res.url);
                  ToastUtil.remove(id);
                }}
              >
                {res.message}
              </Button>
            );
          if (res.type == SystemNotificationType.Error) {
            ToastUtil.error(res.message, { duration: Infinity });
          } else if (res.type == SystemNotificationType.Success) {
            ToastUtil.success(content);
          } else {
            if (res.requireDismiss)
              ToastUtil.setMessage('information', res.title, content, [], null, {
                duration: Infinity,
                id
              });
            else
              ToastUtil.setMessage('information', res.title, content, [], null, {
                duration: 5000,
                id
              });
          }
        }
      }
    });
    client.start();
    return client;
  });

  useUnmountEffect(() => {
    serverEventsClient?.stop();
  });

  return null;
};

export default ApiNotifications;
